import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import {
  Search,
  Phone,
  MessageCircle,
  Trash2,
  RefreshCw,
  Filter,
  Calendar,
  Users,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import AdminNav from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(s) {
  try {
    const d = new Date(s);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
}

export default function AdminLeads() {
  const { user, authHeader, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: authHeader });
      setLeads(data || []);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Could not load leads";
      toast.error(msg);
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, logout]);

  useEffect(() => {
    if (user) fetchLeads();
  }, [user, fetchLeads]);

  const courses = useMemo(() => {
    const set = new Set(leads.map((l) => l.course).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [leads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      const matchesQ = !q
        || l.name.toLowerCase().includes(q)
        || l.phone.toLowerCase().includes(q)
        || (l.message || "").toLowerCase().includes(q)
        || (l.source || "").toLowerCase().includes(q);
      const matchesC = courseFilter === "all" || l.course === courseFilter;
      return matchesQ && matchesC;
    });
  }, [leads, query, courseFilter]);

  const stats = useMemo(() => {
    const today = new Date().toDateString();
    const todayCount = leads.filter((l) => new Date(l.created_at).toDateString() === today).length;
    const week = leads.filter((l) => {
      const diff = (Date.now() - new Date(l.created_at).getTime()) / (1000 * 60 * 60 * 24);
      return diff <= 7;
    }).length;
    return { total: leads.length, todayCount, week };
  }, [leads]);

  const onDelete = async (id) => {
    if (!confirm("Delete this lead permanently?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeader });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Delete failed";
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <Loader2 className="w-8 h-8 animate-spin text-[#002570]" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <AdminNav />

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Admin Dashboard</p>
            <h1 className="mt-2 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a]">Lead Inbox</h1>
            <p className="mt-2 font-inter text-sm text-slate-600">Every consultation request your website has captured.</p>
          </div>
          <button
            onClick={fetchLeads}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-inter font-medium text-slate-700 hover:text-[#002570] rounded-full hover:bg-slate-100 transition-colors self-start md:self-auto"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: Users, label: "Total Leads", value: stats.total, accent: "#002570" },
            { icon: Calendar, label: "Today", value: stats.todayCount, accent: "#73c3ff" },
            { icon: Filter, label: "Last 7 Days", value: stats.week, accent: "#002570" },
          ].map(({ icon: Icon, label, value, accent }) => (
            <div key={label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${accent}15`, color: accent }}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                <p className="font-rubik text-2xl font-bold text-[#0f172a]">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search-input"
                placeholder="Search by name, phone, message…"
                className="w-full bg-[#f8fafc] rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
              />
            </div>
            <select
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              data-testid="admin-course-filter"
              className="bg-[#f8fafc] rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition md:min-w-[200px]"
            >
              {courses.map((c) => (
                <option key={c} value={c}>{c === "all" ? "All Courses" : c}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading leads…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 px-6">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#73c3ff]/15 text-[#002570] flex items-center justify-center mb-4">
                <Users className="w-6 h-6" />
              </div>
              <p className="font-rubik text-lg font-semibold text-[#0f172a]">No leads to show</p>
              <p className="mt-1 font-inter text-sm text-slate-500">
                {leads.length === 0 ? "Once visitors submit the website form, they'll appear here." : "No results for the current filters."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-leads-table">
                <thead className="bg-[#f8fafc] border-b border-slate-200">
                  <tr>
                    {["Name", "Phone", "Course", "Message", "Source", "Date", "Actions"].map((h) => (
                      <th key={h} className="text-left font-inter text-xs font-semibold uppercase tracking-wider text-slate-500 px-4 md:px-5 py-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => {
                    const waText = `Hi ${l.name}! Thanks for reaching out to Educators Institute about ${l.course}. When is a good time for a quick call?`;
                    const waLink = `https://wa.me/${l.phone.replace(/\D/g, "")}?text=${encodeURIComponent(waText)}`;
                    return (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-100 hover:bg-[#f8fafc] transition-colors">
                        <td className="px-4 md:px-5 py-4 font-rubik font-semibold text-[#0f172a] whitespace-nowrap">{l.name}</td>
                        <td className="px-4 md:px-5 py-4 font-inter text-sm text-slate-700 whitespace-nowrap">{l.phone}</td>
                        <td className="px-4 md:px-5 py-4">
                          <span className="inline-block bg-[#73c3ff]/15 text-[#002570] text-xs font-inter font-medium px-3 py-1.5 rounded-full whitespace-nowrap">{l.course}</span>
                        </td>
                        <td className="px-4 md:px-5 py-4 font-inter text-sm text-slate-600 max-w-[280px] truncate" title={l.message}>{l.message || <span className="text-slate-400 italic">—</span>}</td>
                        <td className="px-4 md:px-5 py-4 font-inter text-xs text-slate-500 whitespace-nowrap">{l.source}</td>
                        <td className="px-4 md:px-5 py-4 font-inter text-xs text-slate-500 whitespace-nowrap">{formatDate(l.created_at)}</td>
                        <td className="px-4 md:px-5 py-4">
                          <div className="flex items-center gap-2">
                            <a
                              href={waLink}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`lead-wa-${l.id}`}
                              className="w-9 h-9 rounded-full bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366] hover:text-white flex items-center justify-center transition-colors"
                              aria-label="WhatsApp"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                            <a
                              href={`tel:${l.phone}`}
                              data-testid={`lead-call-${l.id}`}
                              className="w-9 h-9 rounded-full bg-[#002570]/10 text-[#002570] hover:bg-[#002570] hover:text-white flex items-center justify-center transition-colors"
                              aria-label="Call"
                            >
                              <Phone className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => onDelete(l.id)}
                              disabled={deletingId === l.id}
                              data-testid={`lead-delete-${l.id}`}
                              className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center transition-colors disabled:opacity-60"
                              aria-label="Delete"
                            >
                              {deletingId === l.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-6 font-inter text-xs text-slate-400 text-center">Showing {filtered.length} of {leads.length} leads</p>
      </main>
    </div>
  );
}
